import { SocialLogin } from '@capgo/capacitor-social-login';
import { setGlobalCurrentClinic } from 'boot/currentClinic';
import {
  auth, callFirebaseFunction, db,
} from 'boot/firebase';
import { i18n, loadLanguageAsync } from 'boot/i18n';
import { clearIndexedDb, clearIndexedDbTable, clearOfflineModeData } from 'boot/indexedDb';
import { setGlobalOfflineMode, setGlobalOfflineModeAvailable } from 'boot/network';
import { clearAllViewState } from 'components/UI/recordsViewState';
import {
  createUserWithEmailAndPassword, EmailAuthProvider, getIdToken,
  getIdTokenResult, linkWithCredential, multiFactor, onAuthStateChanged, PhoneAuthProvider,
  PhoneMultiFactorGenerator, reauthenticateWithCredential, reload, sendEmailVerification,
  sendPasswordResetEmail, signInWithEmailAndPassword, signOut, TotpMultiFactorGenerator, updateEmail,
  updatePassword, verifyBeforeUpdateEmail,
} from 'firebase/auth';
import { disableNetwork, enableNetwork } from 'firebase/firestore';
import {
  cloneDeep, concat, find, get, head, isArray, isBoolean,
  isEmpty, isNil, isNumber, isObject, keys, map,
} from 'lodash';
import { notifyInfo, notifyNegative, notifyWarning } from 'src/composables/interactions';
import { CANCELLATION_TYPES } from 'src/constants/appointments';
import {
  AUTH_ERROR, AUTH_SESSION_WORKER_COMMANDS, AUTH_SESSION_WORKER_EVENTS, AUTH_SESSIONS_STATUS,
  MFA_METHODS, SSO_PROVIDER, WARNING_DELAY_BEFORE_AUTO_LOGOUT,
} from 'src/constants/authorization';
import { BROADCAST_MESSAGES } from 'src/constants/broadcastChannel';
import { ENCRYPTION_OBJECT_STORE } from 'src/constants/encryption';
import { V2_DRIVEN_INTEGRATIONS } from 'src/constants/integrations';
import { SESSION_ACTIVITY_TYPES } from 'src/constants/sessionActivities';
import { FCM_TOKEN_MAX_COUNT } from 'src/constants/users';
import ActivityTypesDB from 'src/fb/activityTypes-db';
import AppointmentsDB from 'src/fb/appointments-db';
import BillingDB from 'src/fb/billing-db';
import ClientsDB from 'src/fb/clients-db';
import ClinicsDB from 'src/fb/clinics-db';
import ClinicSettingsDB from 'src/fb/clinicSettings-db';
import ClinicSettingsUpdatesDB from 'src/fb/clinicSettingsUpdates-db';
import TreatmentServicesDB from 'src/fb/treatmentServices-db';
import PlacesOfServiceDB from 'src/fb/placesOfService-db';
import UserInvitationsDB from 'src/fb/userInvitations-db';
import UserPrivilegesDB from 'src/fb/userPrivileges-db';
import UsersDB from 'src/fb/users-db';
import UsersClientsDB from 'src/fb/usersClients-db';
import UserSettingsDB from 'src/fb/userSettings-db';
import GenericIndexedDb from 'src/indexed-db/generic-indexedDb';
import { assessments as useAssessmentsStore } from 'src/store/assessments';
import { broadcastChannel as useBroadcastChannelStore } from 'src/store/broadcastChannel';
import { chartFavorites as useChartFavoritesStore } from 'src/store/chartFavorites';
import { clients as useClientsStore } from 'src/store/clients';
import { dataFields as useDataFieldsStore } from 'src/store/dataFields';
import { firestoreUnbindings as useFirestoreUnbindingsStore } from 'src/store/firestoreUnbindings';
import { integrationReferences as useIntegrationReferencesStore } from 'src/store/integrationReferences';
import { network as useNetworkStore } from 'src/store/network';
import { notifications as useNotificationsStore } from 'src/store/notifications';
import { programBehaviourData as useProgramBehaviourDataStore } from 'src/store/programBehaviourData';
import { programs as useProgramsStore } from 'src/store/programs';
import { promptLevels as usePromptLevelsStore } from 'src/store/promptLevels';
import { region as useRegionStore } from 'src/store/region';
import { serviceWorker as useServiceWorkerStore } from 'src/store/serviceWorker';
import { sessions as useSessionsStore } from 'src/store/sessions';
import { tags as useTagsStore } from 'src/store/tags';
import { treatmentPlans as useTreatmentPlansStore } from 'src/store/treatmentPlans';
import { userInterface as useUserInterfaceStore } from 'src/store/userInterface';
import { users as useUsersStore } from 'src/store/users';
import { workflows as useWorkflowsStore } from 'src/store/workflows';
import AppUtils, { userRoles } from 'src/utils/AppUtils';
import { addBackupDecryptionKey } from 'src/utils/backupUtils';
import { readSecret } from 'src/utils/CryptoUtils';
import { bindDataBaseStore, bindIndexedDbStore, unbindDataBaseStore } from 'src/utils/FirestoreUtils';
import { sendEventToAloha, updateAppointmentUserReference } from 'src/utils/Integrations';
import { logEvent } from 'src/utils/LogEvent';
import { syncToOffline } from 'src/utils/OfflineAccessUtils';
import { appAnalytics } from 'src/services/analytics';

/** @typedef {import('firebase/auth').UserCredential} UserCredential */
/** @typedef {import('firebase/auth').MultiFactorResolver} MultiFactorResolver */

async function findAndNotifyUserDeletion(email) {
  const { data } = await callFirebaseFunction('findDeletedUser', { email });
  const deletedTimestamp = get(data, ['deleteTimestamp'], null);

  if (deletedTimestamp) {
    // timezone not used as this is performed on auth error, meaning we don't have access to user selected timezone
    // eslint-disable-next-line no-restricted-syntax
    const deletedDate = new Date(deletedTimestamp).toLocaleDateString();
    notifyNegative(i18n.t('login.notifyUserWasDeleted', { deletedDate }));

    return true;
  }

  return false;
}

let notificationBanner = null;
let lastIntegrationSettingsRefresh = 0;
/**
 * The pending treatmentServices bind, so concurrent first-callers reuse it instead
 * of issuing another bind while the initial call is still in flight.
 * @type {Promise<void>|null}
 */
let treatmentServicesBindPromise = null;
/**
 * True once treatmentServices has been bound this session, so ensureTreatmentServicesBound
 * short-circuits repeat calls and logout resets it for the next user on the same tab.
 * (Not needed for offline-mode transitions: those reload the page, which resets this
 * flag and lets treatmentServices re-bind lazily in the new mode.)
 * @type {boolean}
 */
let treatmentServicesBound = false;
/**
 * The pending placesOfService bind, so concurrent first-callers reuse it instead
 * of issuing another bind while the initial call is still in flight.
 * @type {Promise<void>|null}
 */
let placesOfServiceBindPromise = null;
/**
 * True once placesOfService has been bound this session, so ensurePlacesOfServiceBound
 * short-circuits repeat calls and logout resets it for the next user on the same tab.
 * (Not needed for offline-mode transitions: those reload the page, which resets this
 * flag and lets placesOfService re-bind lazily in the new mode.)
 * @type {boolean}
 */
let placesOfServiceBound = false;

export default {
  initTimeComposable(composable) {
    this.timeComposable = composable;
  },

  setUserDetails(payload) {
    this.userDetails = payload;
    setGlobalCurrentClinic(payload?.clinic);
  }, // setUserDetails()

  setEmailVerified(payload) {
    this.emailVerified = payload;
  }, // setEmailVerified()

  setNewUserInfo(userInfo) {
    this.newUserInfo = userInfo;
    localStorage.setItem('auth_newUserInfo', JSON.stringify(this.newUserInfo));
  }, // setNewUserInfo()

  setConnectedClinic(connectedClinic = {}) {
    this.connectedClinic = connectedClinic;
  }, // setConnectedClinic()

  setCurrentClient(currentClient = {}) {
    this.currentClient = { ...currentClient };

    if (currentClient.id) {
      this.currentClient.id = currentClient.id;
    }
  }, // setCurrentClient()

  setClaims(claims) {
    this.claims = claims;
  }, // setClaims()

  setBilling(billing) {
    this.billing = billing;
  }, // setBilling()

  setRememberMe(rememberMe) {
    this.rememberMe = rememberMe;
    localStorage.setItem('auth_rememberMe', rememberMe);
  }, // setRememberMe()

  setAllUserProviders(providers) {
    this.allUserProviders = providers;
  }, // setAllUserProviders()

  setUserSettings(userSettings) {
    this.userSettings = userSettings;
  }, // setUserSettings()

  setProvider(provider) {
    this.provider = provider;
    localStorage.setItem('login_provider', provider);
  }, // setProvider()

  setLoadPastUserAppointments(loadPastUserAppointments) {
    this.loadPastUserAppointments = loadPastUserAppointments;
  }, // setLoadPastUserAppointments()

  setUserAppointmentsLimitMutation(userAppointmentsLimit) {
    this.userAppointmentsLimit = userAppointmentsLimit;
  }, // setUserAppointmentsLimitMutation()

  setUserPrivilegeLabels(userPrivilegeLabels) {
    this.userPrivilegeLabels = userPrivilegeLabels;
  }, // setUserPrivilegeLabels()

  setAllowImmediateSync(allowImmediateSync) {
    this.allowImmediateSync = allowImmediateSync;
  }, // setAllowImmediateSync()

  setAppointmentThreshold(appointmentThreshold) {
    this.appointmentThreshold = appointmentThreshold;
  }, // setAppointmentThreshold()

  setCareModelCohorts(careModelCohorts) {
    this.careModelCohorts = careModelCohorts;
  }, // setCareModelCohorts()

  setCancellationReasons(cancellationReasons) {
    this.cancellationReasons = cancellationReasons;
  }, // setCancellationReasons()

  setUseSessionPrototypes(useSessionPrototypes) {
    this.useSessionPrototypes = useSessionPrototypes;
  }, // setUseSessionPrototypes()

  setStrictSessionNaming(strictSessionNaming) {
    this.strictSessionNaming = strictSessionNaming;
  }, // setStrictSessionNaming()

  setEVVRequired(evvRequired) {
    this.evvRequired = evvRequired;
  }, // setEVVRequired()

  setUseLegalNameInSignature(useLegalNameInSignature) {
    this.useLegalNameInSignature = useLegalNameInSignature;
  }, // setUseLegalNameInSignature

  setUseBetaProgramImport(useBetaProgramImport) {
    this.useBetaProgramImport = useBetaProgramImport;
  }, // setUseBetaProgramImport()

  setAutoImportClients(autoImportClients) {
    this.autoImportClients = autoImportClients;
  }, // setUseBetaProgramImport()

  setClinicNotificationSettings(clinicNotificationSettings) {
    this.clinicNotificationSettings = clinicNotificationSettings;
  }, // setClinicNotificationSettings()

  setBusinessHoursSlots(businessHoursSlots) {
    this.businessHoursSlots = businessHoursSlots;
  }, // setBusinessHoursSlots()

  setSessionNotesLayoutSettings(sessionNotesLayoutSettings) {
    this.sessionNotesLayoutSettings = sessionNotesLayoutSettings;
  }, // setSessionNotesLayoutSettings()

  setActivityTypesSettings(activityTypesSettings) {
    this.activityTypesSettings = activityTypesSettings;
  }, // setActivityTypesSettings()

  setIntegrationSettings(integrationSettings) {
    this.integrationSettings = integrationSettings;
  }, // setIntegrationSettings()

  setShowDefaultTreatmentAreas(showDefaultTreatmentAreas) {
    this.showDefaultTreatmentAreas = showDefaultTreatmentAreas;
  }, // setShowDefaultTreatmentAreas()

  setShowDefaultProgramDataFields(showDefaultProgramDataFields) {
    this.showDefaultProgramDataFields = showDefaultProgramDataFields;
  }, // setShowDefaultProgramDataFields()

  setAuthSessionsDuration(authSessionsDuration) {
    this.authSessionsDuration = authSessionsDuration;
  }, // setAuthSessionsDuration()

  setAuthSessionStatus(authSessionStatus) {
    this.authSessionStatus = authSessionStatus;
  }, // setAuthSessionStatus()

  /**
   * Disables auto-unselect disabled clients logic for the specific client
   * @see {useClientsTriggerNotifications}
   *
   * @param {string} clientId
   */
  setDeactivatedClientAllowedToView(clientId) {
    this.deactivatedClientAllowedToView = clientId;
  }, // setDeactivatedClientAllowedToView()

  setTrajectorySettings(trajectorySettings) {
    this.trajectorySettings = trajectorySettings;
  },

  initializeStore() {
    try {
      const newUserInfo = localStorage.getItem('auth_newUserInfo');
      if (newUserInfo) {
        this.setNewUserInfo(JSON.parse(newUserInfo));
      }
    } catch (err) {
      logEvent(err);
      // Something went wrong - make sure we don't have incomplete data hanging
      this.setUserDetails({});
      this.setEmailVerified(null);
      this.setConnectedClinic({});
      this.setCurrentClient({});
      this.setClaims({});
      this.setBilling({});
    }
  }, // initializeStore()

  async registerUser(payload) {
    const userObj = {
      userId: null,
      name: payload.name.trim(),
      email: payload.email.toLowerCase().trim(),
      clinic: null,
      role: payload.role || null,
      units: [],
      status: 'active',
      admin: false,
      acceptMarketing: !!payload.acceptMarketing,
    };

    if (payload.initialPackageUrlShortCode) {
      userObj.initialPackageUrlShortCode = payload.initialPackageUrlShortCode;
    }

    this.setNewUserInfo(userObj);
    const newUser = await createUserWithEmailAndPassword(
      auth,
      userObj.email,
      payload.password,
    );
    await reload(auth.currentUser);

    if (!JSON.parse(localStorage.getItem('userAuthenticated'))) {
      // register new clinic, user and set authentication token
      localStorage.setItem('userAuthenticated', 'true');
    }
    return newUser;
  }, // registerUser()

  async login(payload) {
    this.setRememberMe(payload.rememberMe ? 'true' : '');

    const authResult = await signInWithEmailAndPassword(
      auth,
      payload.email.toLowerCase().trim(),
      payload.password,
    );

    if (payload.rememberEmail) {
      localStorage.setItem('remember_email', payload.email.toLowerCase().trim());
    } else {
      localStorage.removeItem('remember_email');
    }

    if (this.pendingCredential) {
      try {
        await linkWithCredential(authResult.user, this.pendingCredential);
      } catch (err) {
        console.error('Microsoft linking failed:', err);
        logEvent(err);
      } finally {
        this.setPendingCredential(null);
      }
    }
    localStorage.setItem('userAuthenticated', 'true');

    return authResult;
  }, // login()

  async logout() {
    this.removeAuthSessionNotification();
    this.unbindAuthSessions();
    this.destroyAuthSessionWorker();

    // Tear down the lazily-bound, clinic-scoped treatmentServices listener and
    // reset its state so the next user on the same tab starts fresh.
    this.unbindTreatmentServices();

    // Tear down the lazily-bound, clinic-scoped placesOfService listener and
    // reset its state so the next user on the same tab starts fresh.
    this.unbindPlacesOfService();

    localStorage.removeItem('userAuthenticated');
    sessionStorage.removeItem('selectedClient');

    setGlobalOfflineMode(false);
    setGlobalOfflineModeAvailable(false);

    await clearIndexedDb();

    if (process.env.MODE === 'capacitor' && this.provider === SSO_PROVIDER.GOOGLE) {
      await SocialLogin.logout({ provider: 'google' });
    }

    const network = useNetworkStore();
    const currentMessagingToken = localStorage.getItem('currentMessagingToken');
    const fcmTokens = get(this.userDetails, 'fcmTokens', []);
    const isOnlineModeWithoutNetwork = !network.networkOnline && !network.offlineMode;

    if (currentMessagingToken
      && fcmTokens.find(({ value }) => value === currentMessagingToken)
      && !isOnlineModeWithoutNetwork
    ) {
      try {
        const usersDb = new UsersDB();
        const updatedTokens = fcmTokens.filter(({ value }) => value !== currentMessagingToken);
        await usersDb.set({ fcmTokens: updatedTokens }, this.userDetails.userId);
      } catch (err) {
        console.error('Unable to delete messaging token', err);
        logEvent(err);
      } finally {
        localStorage.removeItem('currentMessagingToken');
      }
    }

    await signOut(auth);
    appAnalytics.resetUser();

    const broadcastChannel = useBroadcastChannelStore();
    await broadcastChannel.sendBroadcastMessage(BROADCAST_MESSAGES.USER_LOGOUT_BROADCAST_MESSAGE);

    if (!this.isAuthSessionExpired) {
      // Do not clear the "expired" status here.
      // It is used as a temporary redirect flag to bypass beforeRouteLeave
      // unsaved-changes prompts until navigation to the login page is finished.
      this.setAuthSessionStatus(null);
    }
  }, // logout()

  async emailIsVerified() {
    if (this.emailVerified) {
      return true;
    }

    if (!auth.currentUser) {
      return false;
    }

    await reload(auth.currentUser);

    if (auth.currentUser.emailVerified) {
      this.setEmailVerified(true);
      await this.acceptInvitations();
      await this.autoSelectClient();
    }

    return auth.currentUser.emailVerified;
  }, // emailIsVerified()

  async sendResetPasswordLink(payload) {
    return sendPasswordResetEmail(auth, payload.email);
  }, // sendResetPasswordLink()

  async acceptInvitations(context) {
    const network = useNetworkStore();
    // Guard: Only users that are verified and clinic users cannot receive invitations
    // Platform admins belong to no clinic by design, so the checks below would read them as a
    // half-finished invitation signup and reject the login with "invitation not found".
    if (this.isManager || this.userDetails.admin || this.userDetails.sysAdmin || network.offlineMode) {
      return;
    }

    const userInvitationsDb = new UserInvitationsDB();
    const userInvitationObj = await userInvitationsDb.read(this.userDetails.email);

    const region = useRegionStore();
    let invitationExistsRegion;
    if (isNil(userInvitationObj)) {
      if (this.userDetails.clinic || this.userDetails.admin) {
        return;
      }

      const checkResult = await callFirebaseFunction('checkInvitation', {
        email: this.userDetails.email,
        provider: this.provider,
      });

      if (checkResult.data.exists && checkResult.data.region !== region.region) {
        invitationExistsRegion = checkResult.data.region;
      }
    }

    // Accept all invitations
    const authStateFields = {
      userDetails: this.userDetails,
      provider: this.provider,
      ssoProviders: this.ssoProviders,
    };
    const result = await callFirebaseFunction('acceptInvitations', {
      authStateFields,
      invitationExistsRegion,
    });

    if (get(result.data, 'failed', false)) {
      localStorage.removeItem('userAuthenticated');
      this.setUserDetails({});

      if (get(result.data, 'region', false)) {
        await region.setRegion({ region: result.data.region, context });
      }

      throw new Error(get(result.data, 'message', ''));
    }

    // Prepare UsersClients object for later use
    if (!this.userDetails.clinic && !userInvitationObj?.grantAdmin) {
      // Set clinic and role
      const usersDb = new UsersDB();
      const userObj = {
        clinic: userInvitationObj.clinic,
        role: userInvitationObj.role,
        userPrivilege: userInvitationObj.userPrivilege || null,
        staffMember: userInvitationObj.staffMember || null,
        integrationReference: userInvitationObj.integrationReference || null,
        requireMFA: userInvitationObj.requireMFA || false,
        units: userInvitationObj.units || [],
        name: userInvitationObj.name || this.userDetails.name || '',
      };
      await usersDb.set(userObj, this.userDetails.userId);
      await callFirebaseFunction('createContact', {
        clinicId: userInvitationObj.clinic,
        stagingCreateHubspotContact: userInvitationObj.stagingCreateHubspotContact,
        acceptMarketing: this.userDetails?.acceptMarketing,
        contactRegion: region.region,
      });

      if (userObj.integrationReference && userObj.clinic) {
        const clinicsDb = new ClinicsDB();
        const connectedClinic = await clinicsDb.read(userObj.clinic);

        if (connectedClinic?.integration === 'aloha') {
          await AppUtils.allSettled([
            updateAppointmentUserReference(connectedClinic.id, userObj.integrationReference, {
              userId: this.userDetails.userId,
              userName: this.userDetails.name,
            }),
            sendEventToAloha(connectedClinic.id, 'MemberMappingsAdded', { refId: userObj.integrationReference }),
          ]);
        }

        // V2 PMs (Camber/Boost) schedule staff before they exist in Hi Rasmus, so their
        // appointments land orphaned (user === null). The user doc now exists with its
        // integrationReference, so back-link those rows.
        if (V2_DRIVEN_INTEGRATIONS.includes(connectedClinic?.integration)) {
          // Await so the request completes before post-acceptance navigation can drop it;
          // swallow failures so a backfill error never blocks acceptance (mirrors Aloha above).
          try {
            await callFirebaseFunction('backfillV2AppointmentsForRef', {
              clinicId: connectedClinic.id,
              refId: userObj.integrationReference,
              objectType: 'staff',
            });
          } catch (err) {
            console.error('backfillV2AppointmentsForRef failed', err);
          }
        }
      }
    }
    // Clear userInvitationsDb
    // this action should be done only after creating user in collection/users
    // deleting invitation triggers deleteUserInvitation
    await userInvitationsDb.deleteSubcollection(this.userDetails.email, 'invitationClients');
    await userInvitationsDb.delete(this.userDetails.email);
  }, // acceptInvitations()

  async autoSelectClient() {
    const previousSelectedClient = sessionStorage.getItem('selectedClient');

    if (previousSelectedClient) {
      return;
    }

    const associatedClientsExceptStaff = this.associatedClients?.filter(({ client }) => {
      return client !== this.userDetails.staffMember;
    }) || [];
    // If user only has one client then select it automatically
    if (!this.isManagerOrAdministrator && (associatedClientsExceptStaff.length === 1)) {
      const clientId = associatedClientsExceptStaff[0].client;

      if (clientId) {
        const clientsDb = new ClientsDB();
        const client = await clientsDb.read(clientId);

        if (client?.status === 'active') {
          await this.changeCurrentClient(client);
          return;
        }
      }
    }

    const ownStaffProfile = this.userDetails?.staffMember;
    if (ownStaffProfile) {
      const staffProfileAssociation = this.associatedClients.find((association) => {
        return association.client === ownStaffProfile;
      });
      if (staffProfileAssociation?.client) {
        try {
          const clientsDb = new ClientsDB();
          const client = await clientsDb.read(staffProfileAssociation.client);

          if (client?.status === 'active') {
            await this.changeCurrentClient(client);
          }
        } catch (e) {
          console.error('Error loading a client', e);
        }
      }
    }
  }, // autoSelectClient()

  /**
   * Re-authenticate the user before changing e-mail or password
   */
  reauthenticate({ password }) {
    const credential = EmailAuthProvider.credential(
      auth.currentUser.email,
      password,
    );

    return reauthenticateWithCredential(auth.currentUser, credential);
  }, // reauthenticate()

  /**
   * Update user token and retrieve new custom claims
   */
  async getUserClaims(forceRefresh = false) {
    if (!isEmpty(this.claims) && !forceRefresh) {
      return this.claims;
    }

    const token = await getIdTokenResult(auth.currentUser, true);
    const claims = get(token, 'claims', {});
    this.setClaims(claims);

    return claims;
  }, // getUserClaims()

  /**
   * Update user token and retrieve new custom claims
   */
  async updateUserToken() {
    await getIdToken(auth.currentUser, true);
    await this.getUserClaims(true);
  }, // updateUserToken()

  /**
   * Change user's e-mail
   */
  async changeAuthEmail({ email }) {
    await updateEmail(auth.currentUser, email);

    const isLocalhost = window.location.hostname === 'localhost' && process.env.MODE !== 'capacitor';

    return sendEmailVerification(auth.currentUser, {
      url: isLocalhost ? AppUtils.ORIGINS.STAGING : AppUtils.getCurrentOrigin(),
    });
  }, // changeAuthEmail()

  /**
   * Change MFA user's e-mail
   */
  async changeMFAUserAuthEmail({ email }) {
    try {
      await this.unverifyEmail();

      const isLocalhost = window.location.hostname === 'localhost' && process.env.MODE !== 'capacitor';

      await verifyBeforeUpdateEmail(auth.currentUser, email, {
        url: isLocalhost ? AppUtils.ORIGINS.STAGING : AppUtils.getCurrentOrigin(),
      });
    } catch (err) {
      console.error(`MFA verifyBeforeUpdateEmail error: ${err}`);
      logEvent(err);
    }
  }, // changeMFAUserAuthEmail()

  /**
   * Change user's password
   */
  changeAuthPassword({ newPassword }) {
    return updatePassword(auth.currentUser, newPassword);
  }, // changeAuthPassword()

  /**
   * Set password to user registered with 3d party provider
   */
  async setAuthPassword({ email, newPassword }) {
    const credential = EmailAuthProvider.credential(email, newPassword);
    const updatedUserCred = await linkWithCredential(auth.currentUser, credential);

    this.setAllUserProviders(concat(this.allUserProviders, ['password']));

    return updatedUserCred;
  }, // setAuthPassword()

  async sendPhoneMfaEnrollSMS({ phoneNumber, recaptchaVerifier }) {
    const user = auth.currentUser;
    const session = await multiFactor(user).getSession();

    const phoneOpts = {
      phoneNumber,
      session,
    };

    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneOpts,
      recaptchaVerifier,
    );

    return { verificationId };
  }, // sendPhoneMfaEnrollSMS()

  async generateAuthenticatorAppMfaEnrollMethods() {
    try {
      const user = auth.currentUser;
      const session = await multiFactor(user).getSession();

      const totpSecret = await TotpMultiFactorGenerator.generateSecret(session);
      const totpUrl = totpSecret.generateQrCodeUrl(
        user.email,
        'Hi Rasmus',
      );
      return { totpSecret, totpUrl };
    } catch (error) {
      console.error('MFA generation failed', error);
      throw error; // Re-throw the error to be handled by the caller
    }
  }, // generateAuthenticatorAppMfaEnrollMethods()

  async enrollAuthenticatorAppMFA({ totpSecret, verificationCode }) {
    try {
      const user = auth.currentUser;
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret,
        verificationCode,
      );

      await multiFactor(user).enroll(multiFactorAssertion, MFA_METHODS.AUTHENTICATOR_APP.LABEL);
      const usersDb = new UsersDB();
      await usersDb.set({ enrolledMFA: true }, user.uid);
      await this.reloadUserDetails();

      const broadcastChannel = useBroadcastChannelStore();
      await broadcastChannel.sendBroadcastMessage(BROADCAST_MESSAGES.MFA_ENROLLED_BROADCAST_MESSAGE);
    } catch (error) {
      console.error('MFA enroll failed', error);
      throw error; // Re-throw the error to be handled by the caller
    }
  }, // enrollAuthenticatorAppMFA()

  async enrollPhoneNumberMFA({ verificationId, code }) {
    const cred = PhoneAuthProvider.credential(
      verificationId,
      code,
    );

    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(
      cred,
    );

    const user = auth.currentUser;

    await multiFactor(user).enroll(multiFactorAssertion, MFA_METHODS.PHONE_NUMBER.LABEL);
    const usersDb = new UsersDB();
    await usersDb.set({ enrolledMFA: true }, user.uid);
    await this.reloadUserDetails();

    const broadcastChannel = useBroadcastChannelStore();
    await broadcastChannel.sendBroadcastMessage(BROADCAST_MESSAGES.MFA_ENROLLED_BROADCAST_MESSAGE);
  }, // enrollPhoneNumberMFA()

  async sendPhoneMfaCode({ resolver, recaptchaVerifier }) {
    const phoneOpts = {
      multiFactorHint: resolver.hints[0],
      session: resolver.session,
    };

    const phoneAuthProvider = new PhoneAuthProvider(auth);
    const verificationId = await phoneAuthProvider.verifyPhoneNumber(
      phoneOpts,
      recaptchaVerifier,
    );

    return { verificationId };
  }, // sendPhoneMfaCode()

  /**
   * @param {string} verificationId
   * @param {string} code
   * @param {MultiFactorResolver} resolver
   * @return {Promise<UserCredential>}
   */
  async verifyMFA({ verificationId, code, resolver }) {
    const cred = PhoneAuthProvider.credential(
      verificationId,
      code,
    );

    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(
      cred,
    );

    return resolver.resolveSignIn(multiFactorAssertion);
  }, // verifyMFA()

  async unenrollFromAllMFA() {
    try {
      const user = auth.currentUser;

      const currentFactors = multiFactor(user)?.enrolledFactors;
      if (!isEmpty(currentFactors)) {
        const phoneFactor = currentFactors.find(
          (factor) => factor.factorId === MFA_METHODS.PHONE_NUMBER.ID,
        );

        if (phoneFactor) {
          // Unenroll existing phone number
          await multiFactor(user).unenroll(phoneFactor.uid);
        }

        const authenticationAppFactor = currentFactors.find(
          (factor) => factor.factorId === MFA_METHODS.AUTHENTICATOR_APP.ID,
        );

        if (authenticationAppFactor) {
          // Unenroll the existing authenticator app
          await multiFactor(user).unenroll(authenticationAppFactor.uid);
        }

        await this.reloadUserDetails();
      }
    } catch (error) {
      console.error('MFA unenroll failed', error);
      throw error; // Re-throw the error to be handled by the caller
    }
  }, // unenrollFromAllMFA()

  /**
   * Create a new User object in Firestore after auth user is created
   * @param {boolean} emailVerified
   */
  async createNewUser(emailVerified) {
    if ((this.newUserInfo === null) || (!auth.currentUser.uid)) {
      return;
    }

    let user = null;

    try {
      const usersDb = new UsersDB();
      user = await usersDb.getDocSnapshot(auth.currentUser.uid);

      if (user.exists()) {
        // User already created and accessible
        this.setNewUserInfo(null);
      } else {
        const userId = auth.currentUser.uid;
        /*
         * Only the email registration path sets a status; the SSO branch of onAuthStateChanged never
         * has, and nothing downstream adds one, so those users reach Firestore without the field.
         * Defaulted before the spread so a caller meaning something other than active still wins.
         */
        const userObj = {
          status: 'active',
          ...this.newUserInfo,
          userId,
        };

        await usersDb.create(userObj, userId);
      }

      if (!emailVerified) {
        await sendEmailVerification(auth.currentUser);
      }
    } catch (e) {
      // Return error from Firebase
      throw new Error(e);
    }

    if (!JSON.parse(localStorage.getItem('userAuthenticated'))) {
      // create new user invited by manager and set authentication token
      localStorage.setItem('userAuthenticated', 'true');
    }

    this.setNewUserInfo(null);
  }, // createNewUser()

  async unverifyEmail() {
    await callFirebaseFunction('unverifyEmail', { uid: auth.currentUser.uid });
    await reload(auth.currentUser);
    this.setEmailVerified(null);
  }, // unverifyEmail()

  /**
   * Reloads only user document data and updates it in store
   */
  async reloadUserData() {
    const usersDb = new UsersDB();
    const docSnapshot = await usersDb.getDocSnapshot(auth.currentUser.uid);

    if (!docSnapshot.exists()) {
      notifyNegative(i18n.t('store.reloadUserDetailsError'));
      throw new Error(i18n.t('store.reloadUserDetailsError'));
    }

    const userDetails = docSnapshot.data();
    this.setUserDetails(userDetails);
  }, // reloadUserData()

  /**
   * Reload user details from database to vuex store
   */
  async reloadUserDetails() {
    if (!auth.currentUser) {
      this.setUserDetails({});
      this.setEmailVerified(null);
      this.setConnectedClinic({});
      this.setIntegrationSettings({});
      return;
    }

    // Read basic user details and commit them to store
    const usersDb = new UsersDB();
    const docSnapshot = await usersDb.getDocSnapshot(auth.currentUser.uid);

    if (!docSnapshot.exists()) {
      notifyNegative(i18n.t('store.reloadUserDetailsError'));
      throw new Error(i18n.t('store.reloadUserDetailsError'));
    }
    const userDetails = docSnapshot.data();
    if (get(userDetails, 'role') !== userRoles.PARENT) {
      // Don't allow others than parents Remember-me option
      this.setRememberMe('');
    }

    const network = useNetworkStore();
    if (!network.offlineMode) {
      const user = auth.currentUser;

      // reload user for MFA sync in background tabs
      await user.reload();
      const currentFactors = multiFactor(user).enrolledFactors;

      const ignoreMfa = ['development', 'staging'].includes(process.env.ENV_TYPE) && userDetails.ignoreRequiredMfa;

      if (userDetails.admin && !userDetails.requireMFA && !ignoreMfa) {
        await usersDb.set({ requireMFA: true }, user.uid);
        await this.reloadUserDetails();
      }

      if (userDetails.enrolledMFA && isEmpty(currentFactors)) {
        await usersDb.set({ enrolledMFA: false }, user.uid);
        await this.reloadUserDetails();
      }

      if (!isEmpty(currentFactors) && !userDetails.enrolledMFA) {
        logEvent('Sync error: enrolledMFA is false, but MFA is enrolled', {
          severity: 'info',
          context: {
            context_data: { userId: auth.currentUser.uid },
          },
        });

        await usersDb.set({ enrolledMFA: true }, user.uid);
      }
    }

    this.setUserDetails(userDetails);
    this.setEmailVerified(auth.currentUser.emailVerified);

    if (this.userDetails.language) {
      await loadLanguageAsync(this.userDetails.language);
    }

    // Read info about user's connected clinic and store it
    if (this.userDetails.clinic) {
      await AppUtils.allSettled([
        // loadIntegrationSettings depends on reloadConnectedClinic data
        this.reloadConnectedClinic().then(this.loadIntegrationSettings),
        this.bindBilling(this.userDetails.clinic),
        this.bindClinicSettingsUpdates(this.userDetails.clinic),
        this.bindUserPrivileges({
          clinicId: this.userDetails.clinic,
          role: this.userDetails.role,
          userPrivilege: this.userDetails.userPrivilege,
        }),
        this.loadClinicSettings(),
        // treatmentServices is not bound here — it binds lazily on first use via
        // ensureTreatmentServicesBound(). Offline-mode transitions reload the page,
        // so there's no live listener to re-point at the online/offline source.
        // placesOfService is not bound here — it binds lazily on first use via
        // ensurePlacesOfServiceBound(). Offline-mode transitions reload the page,
        // so there's no live listener to re-point at the online/offline source.
      ]);
    }

    const userSettingsDb = new UserSettingsDB(auth.currentUser.uid);
    const userSettings = await userSettingsDb.read('settings');
    if (!isEmpty(userSettings)) {
      this.setUserSettings(userSettings);
    }

    if (network.offlineMode) {
      const provider = localStorage.getItem('login_provider');
      if (provider) {
        this.setProvider(provider);
      }

      return;
    }

    // Set initial claims
    const token = await getIdTokenResult(auth.currentUser, false);
    const claims = get(token, 'claims', {});
    const provider = token.signInProvider === 'custom'
      ? SSO_PROVIDER.MICROSOFT
      : token.signInProvider;
    this.setClaims(claims);
    this.setProvider(provider);
  }, // reloadUserDetails()

  /**
   * Reload connectedClinic details from database to vuex store
   */
  async reloadConnectedClinic() {
    if (this.userDetails.clinic) {
      const clinicsDb = new ClinicsDB();
      const connectedClinic = await clinicsDb.read(this.userDetails.clinic);
      await this.setConnectedClinic(connectedClinic);
    }
  }, // reloadConnectedClinic()

  /**
   * Bind client relationships to current user
   */
  async bindAssociatedClients(userId) {
    const userClientsDb = new UsersClientsDB();
    const ref = userClientsDb.getQuery([['user', '==', userId]]);
    return bindDataBaseStore({ store: this, property: 'associatedClients' }, ref, []);
  }, // bindAssociatedClients

  /**
   * Unbind client relationships to current user
   */
  unbindAssociatedClients() {
    return unbindDataBaseStore({ store: this, property: 'associatedClients' }, []);
  }, // unbindAssociatedClients()

  /**
   * Bind billing object to current user
   */
  async bindBilling(clinicId) {
    const billingDb = new BillingDB();
    const ref = billingDb.getDocRef(clinicId);
    await bindDataBaseStore({ store: this, property: 'billing' }, ref, {});
  }, // bindBilling()

  /**
   * Bind billing object to current user
   */
  async bindClinicSettingsUpdates(clinicId) {
    const clinicSettingsUpdatesDb = new ClinicSettingsUpdatesDB();
    const ref = clinicSettingsUpdatesDb.getDocRef(clinicId);
    await bindDataBaseStore({ store: this, property: 'clinicSettingsUpdates' }, ref, {});
  }, // bindClinicSettingsUpdates()

  /**
   * Unbind billing object
   */
  unbindBilling() {
    unbindDataBaseStore({ store: this, property: 'billing' }, {});
  }, // unbindBilling()

  unbindClinicSettingsUpdates() {
    unbindDataBaseStore({ store: this, property: 'clinicSettingsUpdates' }, {});
  }, // unbindClinicSettingsUpdates()

  async bindUserPrivileges({ clinicId, role, userPrivilege }) {
    if (!role) {
      return;
    }

    const userPrivilegesDb = new UserPrivilegesDB(clinicId);
    const ref = userPrivilegesDb.getDocRef(userPrivilege || role);

    return bindDataBaseStore({ store: this, property: 'userPrivileges' }, ref, {});
  }, // bindUserPrivileges()

  unbindUserPrivileges() {
    return unbindDataBaseStore({ store: this, property: 'userPrivileges' }, {});
  }, // unbindUserPrivileges()

  /**
   * Bind upcoming appointments for current user
   */
  async bindUserAppointments({ userId, clinicId }) {
    if (!userId || !clinicId) {
      return;
    }
    const { adjustDate, endOfDate, addToDate } = this.timeComposable;

    const fromTimestamp = adjustDate(new Date(), { hour: 0, minute: 0, second: 0 });
    const toTimestamp = endOfDate(addToDate(fromTimestamp, { days: 8 }), 'day');

    const appointmentsDb = new AppointmentsDB();
    const constraints = [
      ['clinic', '==', clinicId],
      ['user', '==', userId],
      ['scheduledStartTimestamp', '<=', toTimestamp],
      ['isDeletedSession', '==', false],
    ];

    if (!this.loadPastUserAppointments) {
      constraints.push(['status', 'in', ['scheduled', 'cancelled']]);
      constraints.push(['scheduledStartTimestamp', '>=', fromTimestamp]);
    } else {
      constraints.push(['status', 'in', ['scheduled']]);
    }

    const ref = appointmentsDb.getQuery(
      constraints,
      'scheduledStartTimestamp',
      'asc',
      this.userAppointmentsLimit + 1,
    );

    // return the promise returned by `bindFirestoreRef`
    return bindDataBaseStore({ store: this, property: 'userAppointments' }, ref, []);
  }, // bindUserAppointments()

  /**
   * Unbind appointments for current user
   */
  unbindUserAppointments() {
    this.setLoadPastUserAppointments(false);
    this.setUserAppointmentsLimitMutation(10);
    return unbindDataBaseStore({ store: this, property: 'userAppointments' });
  }, // unbindUserAppointments()

  /**
   * Bind upcoming appointments for current user
   */
  async bindUserAppointmentsInProgress({ userId, clinicId }) {
    if (!userId || !clinicId) {
      return;
    }

    const appointmentsDb = new AppointmentsDB();
    const constraints = [
      ['clinic', '==', clinicId],
      ['user', '==', userId],
      ['status', '==', 'in-progress'],
      ['isDeletedSession', '==', false],
    ];

    const ref = appointmentsDb.getQuery(constraints);

    // return the promise returned by `bindFirestoreRef`
    return bindDataBaseStore({ store: this, property: 'userAppointmentsInProgress' }, ref, []);
  }, // bindUserAppointmentsInProgress()

  /**
   * Unbind appointments for current user
   */
  unbindUserAppointmentsInProgress() {
    return unbindDataBaseStore({ store: this, property: 'userAppointmentsInProgress' });
  }, // unbindUserAppointmentsInProgress()

  switchLoadPastUserAppointments() {
    this.setLoadPastUserAppointments(!this.loadPastUserAppointments);
    return this.bindUserAppointments({ userId: this.userDetails.userId, clinicId: this.userDetails.clinic });
  }, // switchLoadPastUserAppointments()

  setUserAppointmentsLimit(userAppointmentsLimit) {
    this.setUserAppointmentsLimitMutation(userAppointmentsLimit);
    return this.bindUserAppointments({ userId: this.userDetails.userId, clinicId: this.userDetails.clinic });
  }, // setUserAppointmentsLimit()

  /**
   * Bind appointments for current client that are currently in progress
   */
  async bindClientAppointmentsInProgress({ clientId, clinicId }) {
    const appointmentsDb = new AppointmentsDB();
    const ref = appointmentsDb.getQuery([
      ['clinic', '==', clinicId],
      ['client', '==', clientId],
      ['status', '==', 'in-progress'],
      ['isDeletedSession', '==', false],
    ]);

    return bindDataBaseStore({ store: this, property: 'clientAppointmentsInProgress' }, ref, []);
  }, // bindClientAppointmentsInProgress()

  /**
   * Bind upcoming appointments for current client
   */
  async bindClientAppointmentsUpcoming({ clientId, clinicId }) {
    const { adjustDate, endOfDate, addToDate } = this.timeComposable;
    const fromTimestamp = adjustDate(new Date(), { hours: 0, minutes: 0, seconds: 0 });
    const toTimestamp = endOfDate(addToDate(fromTimestamp, { days: 8 }), 'day');

    const appointmentsDb = new AppointmentsDB();
    const ref = appointmentsDb.getQuery([
      ['clinic', '==', clinicId],
      ['client', '==', clientId],
      ['scheduledStartTimestamp', '>=', fromTimestamp],
      ['scheduledStartTimestamp', '<=', toTimestamp],
      ['status', '==', 'scheduled'],
      ['isDeletedSession', '==', false],
    ]);

    return bindDataBaseStore({ store: this, property: 'clientAppointmentsUpcoming' }, ref, []);
  }, // bindClientAppointmentsUpcoming()

  /**
   * Bind recurring appointments for current client
   */
  async bindClientAppointmentsRecurring({ clientId, clinicId }) {
    if (this.connectedClinic.integration || this.hasPremiumScheduling) {
      // Don't bind recurring appointments for clinics with integrations or premium scheduling
      return;
    }

    const appointmentsDb = new AppointmentsDB();
    const ref = appointmentsDb.getQuery([
      ['clinic', '==', clinicId],
      ['client', '==', clientId],
      ['status', '==', 'scheduled'],
      ['recurringWeekdays', '!=', []],
      ['isDeletedSession', '==', false],
    ]);

    return bindDataBaseStore({ store: this, property: 'clientAppointmentsRecurring' }, ref, []);
  }, // bindClientAppointmentsRecurring()

  /**
   * Unbind appointments for current user
   */
  unbindClientAppointmentsInProgress() {
    return unbindDataBaseStore({ store: this, property: 'clientAppointmentsInProgress' });
  }, // unbindClientAppointmentsInProgress()

  unbindClientAppointmentsUpcoming() {
    return unbindDataBaseStore({ store: this, property: 'clientAppointmentsUpcoming' });
  }, // unbindClientAppointmentsUpcoming()

  unbindClientAppointmentsRecurring() {
    return unbindDataBaseStore({ store: this, property: 'clientAppointmentsRecurring' });
  }, // unbindClientAppointmentsRecurring()

  async bindInitUserInfo() {
    const { userId, role, userPrivilege, clinic: clinicId } = this.userDetails;

    if (!userId || !clinicId) {
      return;
    }

    const bindActions = [
      this.bindAssociatedClients(userId),
      this.bindBilling(clinicId),
      this.bindUserPrivileges({ clinicId, role, userPrivilege }),
      this.bindUserAppointments({ userId, clinicId }),
      this.bindUserAppointmentsInProgress({ userId, clinicId }),
      this.bindClinicSettingsUpdates(clinicId),
    ];

    return AppUtils.allSettled(bindActions);
  }, // bindInitUserInfo()

  unbindInitUserInfo() {
    this.unbindAssociatedClients();
    this.unbindBilling();
    this.unbindUserPrivileges();
    this.unbindUserAppointments();
    this.unbindUserAppointmentsInProgress();
    this.unbindClinicSettingsUpdates();
    this.unbindTreatmentServices();
    this.unbindPlacesOfService();
    this.changeCurrentClient({});
  }, // unbindInitUserInfo()

  async loadClinicSettings() {
    // Labels for default cancellation reasons are intentionally hardcoded
    // and not translated because they should not change depending on which
    // user is logged in or the user's language settings.
    const defaultCancellationReasons = [
      {
        id: 'sick',
        cancellationReason: 'Sick',
        cancellationTypes: [CANCELLATION_TYPES.CLIENT, CANCELLATION_TYPES.STAFF],
      },
      {
        id: 'vacation',
        cancellationReason: 'Vacation',
        cancellationTypes: [CANCELLATION_TYPES.CLIENT, CANCELLATION_TYPES.STAFF],
      },
      {
        id: 'holiday',
        cancellationReason: 'Holiday',
        cancellationTypes: [CANCELLATION_TYPES.OTHER],
      },
    ];

    try {
      const clinicSettingsDb = new ClinicSettingsDB();
      const clinicSettingsDoc = await clinicSettingsDb.getDocSnapshot(this.userDetails.clinic);
      if (clinicSettingsDoc.exists()) {
        const clinicSettings = clinicSettingsDoc.data();
        if (isObject(clinicSettings.userPrivilegeLabels)) {
          this.setUserPrivilegeLabels(clinicSettings.userPrivilegeLabels);
        }
        if (isBoolean(clinicSettings.allowImmediateSync)) {
          this.setAllowImmediateSync(clinicSettings.allowImmediateSync);
        }
        if (isNumber(clinicSettings.appointmentThreshold)) {
          this.setAppointmentThreshold(clinicSettings.appointmentThreshold);
        }
        if (isArray(clinicSettings.careModelCohorts)) {
          this.setCareModelCohorts(clinicSettings.careModelCohorts);
        } else {
          this.setCareModelCohorts([]);
        }
        if (isArray(clinicSettings.cancellationReasons)) {
          this.setCancellationReasons(clinicSettings.cancellationReasons);
        } else {
          this.setCancellationReasons(defaultCancellationReasons);
        }
        if (isBoolean(clinicSettings.useSessionPrototypes)) {
          this.setUseSessionPrototypes(clinicSettings.useSessionPrototypes);
        }
        if (isBoolean(clinicSettings.strictSessionNaming)) {
          this.setStrictSessionNaming(clinicSettings.strictSessionNaming);
        }
        if (isBoolean(clinicSettings.evvRequired)) {
          this.setEVVRequired(clinicSettings.evvRequired);
        }
        if (isBoolean(clinicSettings.useLegalNameInSignature)) {
          this.setUseLegalNameInSignature(clinicSettings.useLegalNameInSignature);
        }
        // NAMED BEFORE IT WAS SPLIT. This gated both the new program-import pipeline and the
        // session data importer. The pipeline is no longer optional, so all it still gates is that
        // importer, which is the one part of the import work still in beta. Kept under its stored
        // name rather than renamed: a second clinic setting, a migration and a compatibility
        // fallback is a great deal of machinery for one beta toggle.
        if (isBoolean(clinicSettings.useBetaProgramImport)) {
          this.setUseBetaProgramImport(clinicSettings.useBetaProgramImport);
        }
        if (isBoolean(clinicSettings.autoImportClients)) {
          this.setAutoImportClients(clinicSettings.autoImportClients);
        }
        if (isObject(clinicSettings.notificationSettings)) {
          this.setClinicNotificationSettings(clinicSettings.notificationSettings);
        }
        this.setBusinessHoursSlots(clinicSettings.businessHoursSlots ?? []);
        if (isObject(clinicSettings.sessionNotesLayoutSettings)) {
          this.setSessionNotesLayoutSettings(clinicSettings.sessionNotesLayoutSettings);
        } else {
          this.setSessionNotesLayoutSettings({});
        }
        if (isBoolean(clinicSettings.showDefaultTreatmentAreas)) {
          this.setShowDefaultTreatmentAreas(clinicSettings.showDefaultTreatmentAreas);
        }
        if (isBoolean(clinicSettings.showDefaultProgramDataFields)) {
          this.setShowDefaultProgramDataFields(clinicSettings.showDefaultProgramDataFields);
        }
        if (isBoolean(clinicSettings.isDefaultTagsInitialized)) {
          const clinicsStore = useTagsStore();
          clinicsStore.setIsDefaultTagsInitialized(clinicSettings.isDefaultTagsInitialized);
        }
        if (isNil(clinicSettings.authSessionsDuration) || isNumber(clinicSettings.authSessionsDuration)) {
          this.setAuthSessionsDuration(clinicSettings.authSessionsDuration);
        }
        if (isObject(clinicSettings.trajectorySettings)) {
          this.setTrajectorySettings(clinicSettings.trajectorySettings);
        } else {
          this.setTrajectorySettings({});
        }
      }
    } catch (err) {
      console.error(err.message);
    }

    try {
      const activityTypesDb = new ActivityTypesDB(this.userDetails.clinic);
      const activityTypesSettings = await activityTypesDb.readAll();
      const mergedSettings = { ...SESSION_ACTIVITY_TYPES };
      activityTypesSettings.forEach((entry) => {
        mergedSettings[entry.value] = {
          defaultAppointmentTitle: '',
          ...(SESSION_ACTIVITY_TYPES[entry.value] || {}),
          ...entry,
        };
      });
      this.setActivityTypesSettings(mergedSettings);
    } catch (e) {
      this.setActivityTypesSettings(SESSION_ACTIVITY_TYPES);
    }
  }, // loadClinicSettings()

  async bindTreatmentServices(clinicId) {
    const treatmentServicesDb = new TreatmentServicesDB();
    // Scope to this clinic. getQuery() yields an onSnapshot-able Firestore query online
    // and an IndexedDB query ref offline, so bindDataBaseStore keeps treatmentServices
    // live in both modes.
    const ref = treatmentServicesDb.getQuery([['clinic', '==', clinicId]]);
    await bindDataBaseStore({ store: this, property: 'treatmentServices' }, ref, []);
  }, // bindTreatmentServices()

  unbindTreatmentServices() {
    treatmentServicesBindPromise = null;
    treatmentServicesBound = false;
    this.isLoadingTreatmentServices = false;
    unbindDataBaseStore({ store: this, property: 'treatmentServices' }, []);
  }, // unbindTreatmentServices()

  /**
   * Lazily bind treatment services the first time a consuming surface needs them,
   * deduping concurrent first-callers. Once bound, the live listener keeps the list
   * current for the rest of the session — edits made here or in another session are
   * reflected without an explicit reload. The binding is torn down on logout;
   * offline-mode transitions reload the page, which resets the flag and lets it
   * re-bind lazily in the new mode (see treatmentServicesBound).
   *
   * @returns {Promise<void>|undefined} the pending bind, or undefined if already bound or no clinic.
   */
  async ensureTreatmentServicesBound() {
    if (!this.userDetails?.clinic || treatmentServicesBound) {
      return undefined;
    }

    // Store the in-flight promise so concurrent first-callers share this one bind.
    if (!treatmentServicesBindPromise) {
      treatmentServicesBindPromise = (async () => {
        this.isLoadingTreatmentServices = true;
        try {
          await this.bindTreatmentServices(this.userDetails.clinic);
          treatmentServicesBound = true;
        } catch (error) {
          // Log and rethrow: awaiting callers can react and the next call retries
          // (treatmentServicesBound stays false).
          logEvent(error);
          throw error;
        } finally {
          treatmentServicesBindPromise = null;
          this.isLoadingTreatmentServices = false;
        }
      })();
    }
    return treatmentServicesBindPromise;
  }, // ensureTreatmentServicesBound()

  async bindPlacesOfService(clinicId) {
    const placesOfServiceDb = new PlacesOfServiceDB();
    const ref = placesOfServiceDb.getQuery([['clinic', '==', clinicId]]);
    await bindDataBaseStore({ store: this, property: 'placesOfService' }, ref, []);
  }, // bindPlacesOfService()

  unbindPlacesOfService() {
    placesOfServiceBindPromise = null;
    placesOfServiceBound = false;
    this.isLoadingPlacesOfService = false;
    unbindDataBaseStore({ store: this, property: 'placesOfService' }, []);
  }, // unbindPlacesOfService()

  /**
   * Lazily bind placesOfService the first time an appointment/scheduling/settings
   * surface needs it, deduping concurrent first-callers. Once bound, the live
   * listener keeps the list current for the rest of the session — edits made here
   * or in another session are reflected without an explicit reload. The binding is
   * torn down on logout; offline-mode transitions reload the page, which resets the
   * flag and lets it re-bind lazily in the new mode (see placesOfServiceBound).
   *
   * @returns {Promise<void>|undefined} the pending bind, or undefined if already bound or no clinic.
   */
  async ensurePlacesOfServiceBound() {
    if (!this.userDetails?.clinic || placesOfServiceBound) {
      return undefined;
    }
    if (!placesOfServiceBindPromise) {
      placesOfServiceBindPromise = (async () => {
        this.isLoadingPlacesOfService = true;
        try {
          await this.bindPlacesOfService(this.userDetails.clinic);
          placesOfServiceBound = true;
        } catch (error) {
          // Log and rethrow: awaiting callers can react and the next call retries
          // (placesOfServiceBound stays false).
          logEvent(error);
          throw error;
        } finally {
          placesOfServiceBindPromise = null;
          this.isLoadingPlacesOfService = false;
        }
      })();
    }
    return placesOfServiceBindPromise;
  }, // ensurePlacesOfServiceBound()

  async loadIntegrationSettings() {
    // in some cases reloadUserDetails is called multiple times in rapid succession, we don't need to call this as often
    const isLastRunRecent = (lastIntegrationSettingsRefresh + 10000) > new Date().getTime();
    // getIntegrationData refuses roles without therapist credentials, so the call could only fail
    if (!this.hasTherapistCredentials) {
      this.setIntegrationSettings({});
      return;
    }
    if (!this.userDetails?.clinic || !this.connectedClinic?.integration || isLastRunRecent) {
      return;
    }
    try {
      const { data } = await callFirebaseFunction('getIntegrationData', {
        integration: this.connectedClinic.integration,
        fieldsToReturn: ['isIntegrationSourceOfTruth', 'allowScheduling'],
      });
      const { isIntegrationSourceOfTruth = false, allowScheduling = false } = data;
      this.setIntegrationSettings({ isIntegrationSourceOfTruth, allowScheduling });
      lastIntegrationSettingsRefresh = new Date().getTime();
    } catch (e) {
      console.error(e.message);
    }
  },

  async offlineSync({ ignoreDebounce = false, resetDb = false } = {}) {
    const network = useNetworkStore();
    const userInterface = useUserInterfaceStore();

    // @todo: can we use the Dialog plugin in Vuex? It sounds wrong though
    if (network.offlineMode) {
      return userInterface.setDecryptionKeysDialogOpen(true);
    }

    if (!network.networkOnline) {
      return;
    }

    const { offlineClients } = this.userSettings;
    const checkKey = await readSecret('publicKey');

    const lastSyncData = JSON.parse(localStorage.getItem('lastSyncData'));
    const hasOfflineClients = (Array.isArray(offlineClients) && offlineClients.length > 0);

    if (hasOfflineClients) {
      if (!checkKey) {
        return userInterface.setEncryptionKeysDialogOpen(true);
      }

      await syncToOffline({
        resetDb: resetDb || this.userDetails.userId !== lastSyncData?.userId,
        ignoreSyncDebounce: ignoreDebounce,
      }).catch((err) => {
        notifyNegative(i18n.t('modals.offlineModal.syncingToOfflineError', { message: err.message }));
      });
    }

    if (!hasOfflineClients && lastSyncData) {
      await clearOfflineModeData();
      await clearIndexedDbTable(ENCRYPTION_OBJECT_STORE);
      localStorage.removeItem('lastSyncData');
    }
  }, // offlineSync()

  async handleAuthStateChanged(payload) {
    if (JSON.parse(localStorage.getItem('userAuthenticated'))) {
      this.changeLoaderState(true);
    }

    onAuthStateChanged(auth, async (user) => {
      const userInterface = useUserInterfaceStore();
      const promptLevels = usePromptLevelsStore();
      const dataFields = useDataFieldsStore();
      const treatmentPlans = useTreatmentPlansStore();
      const sessions = useSessionsStore();
      const programs = useProgramsStore();
      const clients = useClientsStore();
      const chartFavorites = useChartFavoritesStore();
      const programBehaviourData = useProgramBehaviourDataStore();
      const workflows = useWorkflowsStore();
      const notifications = useNotificationsStore();
      const users = useUsersStore();
      const assessments = useAssessmentsStore();
      const tags = useTagsStore();
      const firestoreUnbindings = useFirestoreUnbindingsStore();
      const integrationReferences = useIntegrationReferencesStore();

      if (user) {
        const clientsDb = new ClientsDB();
        const offlineClients = await clientsDb.readAll([], null, null, null, null, { forceIndexedDB: true });

        const network = useNetworkStore();

        if (!network.networkOnline && offlineClients.length > 0 && !network.offlineMode) {
          this.setUserDetails({ userId: user.uid });
          network.setForceOffline(true);
          return;
        }

        const usersDb = new UsersDB();

        if (network.networkOnline && network.offlineMode) {
          // Check if user doc exists in indexedDB to make sure that user has downloaded data.
          // If not - switch to online mode and inform a user
          const userFromIdxDb = await usersDb.read(user.uid, { forceIndexedDB: true });
          if (!userFromIdxDb) {
            network.setForceOffline(false);
            network.offlineModeSetStatus(false);
            notifyWarning(i18n.t('modals.offlineModal.noDownloadedDataForOfflineAuth'));
          }
        }

        if (JSON.parse(localStorage.getItem('userAuthenticated')) && !this.showLoader) {
          this.changeLoaderState(true);
        }

        this.setAllUserProviders(map(user.providerData, (data) => data.providerId));

        const providerData = head(user.providerData);

        if ((this.ssoProviders || []).includes(providerData.providerId)) {
          const userName = providerData.displayName || user.displayName || providerData.email;
          const userObj = {
            userId: null,
            name: userName.trim(),
            email: providerData.email?.toLowerCase().trim(),
            clinic: null,
            role: null,
            admin: false,
          };
          this.setNewUserInfo(userObj);
        }

        const doRedirect = isEmpty(this.userDetails);
        // User logged in
        try {
          await this.createNewUser(user.emailVerified);
        } catch (err) {
          // Handle auth error here as well as if this action fails, users stuck with an infinity loader
          await this.handleAuthError({
            context: payload.context,
            user,
            err,
          });
        }

        let retries = 5;
        while (retries > 0) {
          try {
            await this.reloadUserDetails(); // eslint-disable-line no-await-in-loop
            retries = 0;
          } catch (err) {
            retries -= 1;

            if (err.code === 'permission-denied') {
              // force version update after successful login to
              // fix random firestore 'missing permissions' bug
              const serviceWorker = useServiceWorkerStore();
              serviceWorker.refreshApp();
              return;
            }

            if (err.code === 'unavailable') {
              if (retries <= 0) {
                alert('Network connection is unstable. Click OK to refresh the app and retry.');
                window.location.reload();
                return;
              }

              // eslint-disable-next-line no-await-in-loop
              await disableNetwork(db);
              // eslint-disable-next-line no-await-in-loop
              await enableNetwork(db);
            }

            console.error('Error reloading user details', err);

            if (retries <= 0) {
              logEvent(err);
              notifyNegative(err.message);
            }
          }
        }

        const { requireLoginMethod, loginMethod } = this.userDetails;
        if (requireLoginMethod && (loginMethod !== this.provider)) {
          notifyNegative(i18n.t('login.notifyWrongLoginMethodUsed'));
          this.changeLoaderState(false);
          await this.logout();
          return;
        }

        try {
          await this.bindAssociatedClients(user.uid);
          await this.acceptInvitations(payload.context);
          await this.autoSelectClient();
          await Promise.all([
            userInterface.login(),
            promptLevels.login(this.userDetails.clinic),
            dataFields.login(),
            treatmentPlans.login(),
          ]);
          // Need to reload user details, so we could read associated clinic
          // (for creation new account with Google provider)
          await this.reloadUserDetails();
          await this.offlineSync();
          await this.bindUserAppointments({ userId: this.userDetails.userId, clinicId: this.userDetails.clinic });
          await this.bindUserAppointmentsInProgress({
            userId: this.userDetails.userId, clinicId: this.userDetails.clinic,
          });

          if (!network.offlineMode && payload.context.setupPushNotifications) {
            payload.context.setupPushNotifications();
          }

          const encryptionPassword = user.metadata.creationTime;
          await addBackupDecryptionKey(encryptionPassword);

          appAnalytics.identifyUser();
        } catch (err) {
          await this.handleAuthError({
            context: payload.context,
            user,
            err,
          });
          return;
        }

        if (!network.offlineMode) {
          await this.startSessionTimer();
        }

        const preventRedirect = get(payload, ['context', '$route', 'meta', 'preventRedirect'], false);
        if (preventRedirect) {
          this.changeLoaderState(false);
          return;
        }

        if (doRedirect) {
          // @todo: for some reason, payload.context.$route is empty if an authenticated user opens the app
          const { href } = window.location;

          if ((href.indexOf('?') !== -1)) {
            const queryParams = new URLSearchParams(decodeURIComponent(href.slice(href.indexOf('?') + 1)));

            let destination = [...queryParams.entries()].reduce((acc, [key, value]) => {
              if (key === 'redirect') {
                return value;
              }

              if (!acc) {
                return acc;
              }

              return `${acc}&${key}=${value}`;
            }, '');

            if (!destination) {
              const destinationPath = href.slice(href.indexOf('/#/') + 2);

              destination = destinationPath.includes('/login') || destinationPath.includes('/signup')
                ? '/'
                : destinationPath;
            }

            const notificationId = queryParams.get('notificationId');

            if (notificationId && !network.offlineMode) {
              payload.context.setViewed?.(notificationId, true).catch((err) => {
                logEvent(err);
                console.error(err);
              });
            }

            return payload.context.$router.push(destination || '/', async () => {
              this.changeLoaderState(false);
            }, (err) => {
              logEvent(err);
              console.error(err);
            });
          }
        }
        return payload.context.$router.push('/', () => {
          this.changeLoaderState(false);
        });
      }

      // User logged out
      await AppUtils.allSettled([
        this.unbindAssociatedClients(),
        this.unbindBilling(),
        this.unbindUserPrivileges(),
        this.unbindClientAppointmentsInProgress(),
        this.unbindClientAppointmentsUpcoming(),
        this.unbindClientAppointmentsRecurring(),
        this.unbindUserAppointments(),
        this.unbindUserAppointmentsInProgress(),
        this.unbindClinicSettingsUpdates(),
      ]);

      this.setUserDetails({});
      this.setEmailVerified(null);

      // in case of migrated clinic we need to save this value when switching regions
      if (payload.context.$route.name !== 'signup') {
        this.setNewUserInfo(null);
      }

      this.setConnectedClinic({});
      this.setIntegrationSettings({});
      lastIntegrationSettingsRefresh = 0;
      this.setCurrentClient({});
      this.setBilling({});
      this.setClaims({});
      this.setUserSettings({});
      this.setUserPrivilegeLabels({});

      await AppUtils.allSettled([
        sessions.logout(),
        programs.logout(),
        clients.logout(),
        chartFavorites.logout(),
        promptLevels.logout(),
        programBehaviourData.logout(),
        workflows.logout(),
        notifications.logout(),
        users.logout(),
        assessments.logout(),
        tags.logout(),
        integrationReferences.logout(),
        firestoreUnbindings.clearAllUnbindings(),
      ]);

      /*
       * The records pages' stored views and unsaved filters, whoever on this device left them.
       * Keying them by reader stops one person opening another's; it does not stop them sitting on
       * a shared clinic workstation after everyone has gone home.
       */
      clearAllViewState();

      // clear auth, filters and columns keys
      keys(localStorage).forEach((key) => {
        if (
          key.startsWith('auth_')
          || (key.includes('Filter'))
          || (key.includes('Columns'))
          || (key.includes('schedulingSettings_'))
          // TODO - remove videoSessionId/invitedUserId checks later,
          //  it's just a temporary clean-up solution (05.05.2025)
          || (key === 'videoSessionId')
          || (key === 'invitedUserId')
        ) {
          localStorage.removeItem(key);
        }
      });

      this.changeLoaderState(false);

      // Log out user in case of automatic logout
      if (JSON.parse(localStorage.getItem('userAuthenticated'))) {
        this.logout();
      }

      if (!payload.context.$router.currentRoute.value.meta.public) {
        // Force redirect to login page by auto-logout
        if (this.isAuthSessionExpired) {
          await payload.context.$router.push({ name: 'login' });
          this.setAuthSessionStatus(null);
          window.onbeforeunload = null;
          return;
        }

        // Force redirect to login page if user is null and is not on a public route
        if (!isEmpty(payload.context.$router.currentRoute.value.meta)) {
          payload.context.$router.push({ name: 'login' });
        }
      }
    }, (error) => {
      console.error('Auth error', error);
      logEvent(error);
    });
  }, // handleAuthStateChanged()

  /**
   * Persists the current auth session login time in IndexedDB.
   */
  async saveLoginTime(time) {
    const authSessionsDb = new GenericIndexedDb('authSessions');
    await authSessionsDb.create({ loginAt: time }, this.userDetails.userId);
  }, // saveLoginTime()

  /**
   * Binds the persisted auth session data from IndexedDB to the store.
   */
  async bindAuthSessions() {
    const authSessionsDb = new GenericIndexedDb('authSessions');
    const ref = authSessionsDb.getDocRef(this.userDetails.userId);
    await bindIndexedDbStore({ store: this, property: 'loginAt', action: 'setLoginAt' }, ref);
  }, // bindAuthSessions()

  setLoginAt(data) {
    this.loginAt = data?.loginAt ?? null;
  }, // setLoginAt()

  /**
   * Unbinds the persisted auth session data and clears the stored login time.
   */
  unbindAuthSessions() {
    const authSessionsDb = new GenericIndexedDb('authSessions');
    authSessionsDb.unbind(this, 'loginAt');
    this.loginAt = null;
  }, // unbindAuthSessions()

  /**
   * Restores or initializes auth session timing data and starts the session timer worker.
   */
  async startSessionTimer() {
    if (!this.authSessionsDuration || this.userDetails.admin) {
      // "admin" users already have an auto-logout feature implemented on the backend
      return;
    }

    try {
      // Try to restore login time from IndexedDB
      if (!this.loginAt) {
        await this.bindAuthSessions();
      }

      // Initialize login time if no existing auth-session data was found
      if (!this.loginAt) {
        await this.saveLoginTime(Date.now());
      }
    } catch (error) {
      console.error(error);
      return;
    }

    this.removeAuthSessionNotification();
    this.setAuthSessionStatus(AUTH_SESSIONS_STATUS.ACTIVE);
    this.startAuthSessionWorker();
  }, // startSessionTimer()

  /**
   * Updates the auth session status based on the current time and session end time.
   */
  updateAuthSessionStatus(now, endAt) {
    if (!now || !endAt || now >= endAt) {
      this.setAuthSessionStatus(AUTH_SESSIONS_STATUS.EXPIRED);
      return;
    }

    if (now >= endAt - WARNING_DELAY_BEFORE_AUTO_LOGOUT) {
      this.setAuthSessionStatus(AUTH_SESSIONS_STATUS.EXPIRING);
      return;
    }

    this.setAuthSessionStatus(AUTH_SESSIONS_STATUS.ACTIVE);
  }, // updateAuthSessionStatus()

  /**
   * Calculates auth session timing data, including the session end time and formatted remaining time.
   */
  calculateAuthSessionTimeData(now, start, duration) {
    if (!start || !duration) {
      return {};
    }

    const endAt = start + duration;
    const remainingMs = Math.max(0, endAt - now);
    const remainingSeconds = Math.ceil(remainingMs / 1000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;
    const formattedRemainingTime = `${minutes}:${String(seconds).padStart(2, '0')}`;

    return {
      endAt,
      formattedRemainingTime,
    };
  }, // calculateAuthSessionTimeData()

  /**
   * Starts the auth session timer worker with the given tick interval.
   */
  startAuthSessionWorker(intervalMs = 500) {
    this.initAuthSessionWorker();

    this.authSessionWorker.postMessage({
      type: AUTH_SESSION_WORKER_COMMANDS.START,
      payload: { intervalMs },
    });
  }, // startAuthSessionWorker()

  /**
   * Initializes the auth session timer worker and sets up its event handlers.
   */
  initAuthSessionWorker() {
    if (this.authSessionWorker) {
      return;
    }

    this.authSessionWorker = new Worker('/auth-session-timer.web-worker.js');

    this.authSessionWorker.addEventListener('message', (event) => {
      const { type, now } = event.data || {};

      if (type === AUTH_SESSION_WORKER_EVENTS.TICK) {
        this.handleAuthSessionTick(now);
      }
    });

    this.authSessionWorker.addEventListener('error', (error) => {
      console.error(error);
    });
  }, // initAuthSessionWorker()

  destroyAuthSessionWorker() {
    if (!this.authSessionWorker) {
      return;
    }

    this.authSessionWorker.terminate();
    this.authSessionWorker = null;
  }, // destroyAuthSessionWorker()

  /**
   * Processes an auth session timer tick, updating the session status,
   * showing warnings, or logging the user out if needed.
   */
  async handleAuthSessionTick(now = Date.now()) {
    if (this.isAuthSessionExpired) {
      await this.logout();
      this.showPostAutoLogoutNotification();
      return;
    }

    const network = useNetworkStore();
    if (network.offlineMode) {
      this.removeAuthSessionNotification();
      this.destroyAuthSessionWorker();
      this.setAuthSessionStatus(AUTH_SESSIONS_STATUS.ACTIVE);
      return;
    }

    const authSessionTimeData = this.calculateAuthSessionTimeData(now, this.loginAt, this.authSessionsDuration);
    if (isEmpty(authSessionTimeData)) {
      await this.logout();
      return;
    }

    this.updateAuthSessionStatus(now, authSessionTimeData.endAt);

    if (this.isAuthSessionExpiring || this.isAuthSessionExpired) {
      // Even if the session is already expired, still show the warning banner on this tick
      // so the user can see 00:00 before logout on the next tick.
      this.showSessionExpiryWarningNotification(authSessionTimeData.formattedRemainingTime);
    }
  }, // handleAuthSessionTick()

  /**
   * Notify a user that their auth session is about to expire.
   */
  showSessionExpiryWarningNotification(time = '00:00') {
    const broadcastChannel = useBroadcastChannelStore();
    const notificationWarningOptions = {
      group: false,
      timeout: 0,
      actions: [
        {
          label: i18n.t('settings.authentication.authSession.continueAuthSessionBtn'),
          handler: async () => {
            try {
              await broadcastChannel.sendBroadcastMessage(BROADCAST_MESSAGES.CONTINUE_AUTH_SESSION_BROADCAST_MESSAGE);
              await this.saveLoginTime(Date.now());
              await this.startSessionTimer();
            } catch (error) {
              console.error(error);
            }
          },
        },
      ],
    };
    const text = i18n.t('settings.authentication.authSession.notificationWarningMessage');
    const notificationWarningMessage = `${text} ${time}`;

    if (notificationBanner) {
      notificationBanner({ message: notificationWarningMessage });
    } else {
      notificationBanner = notifyWarning(notificationWarningMessage, notificationWarningOptions);
    }
  }, // showSessionExpiryWarningNotification()

  /**
   * Notify a user that they have been logged out due to the auth session ending.
   */
  showPostAutoLogoutNotification() {
    const notificationLogoutMessage = i18n.t('settings.authentication.authSession.notificationLogoutMessage');
    notifyWarning(notificationLogoutMessage, { icon: 'logout' });
  }, // showPostAutoLogoutNotification()

  /**
   * Removes the auth session notification after the session
   * is continued, expired, or the user logs out.
   */
  removeAuthSessionNotification() {
    if (notificationBanner) {
      notificationBanner();
      notificationBanner = null;
    }
  }, // removeAuthSessionNotification()

  /**
   * Updates the auth session duration setting and toggles the auto-logout flow accordingly.
   */
  async updateAuthSessionsDuration(value) {
    if (!this.isManager) {
      return;
    }

    this.removeAuthSessionNotification();
    this.setAuthSessionsDuration(value);

    if (!value) {
      this.destroyAuthSessionWorker();
      this.setAuthSessionStatus(null);
    }

    const clinicSettingsDb = new ClinicSettingsDB();

    await clinicSettingsDb.set(
      { authSessionsDuration: value },
      this.userDetails.clinic,
    );

    if (value) {
      await this.startSessionTimer();
    }
  }, // updateAuthSessionsDuration()

  async handleAuthError(payload) {
    const { context, user, err } = (payload || {});
    logEvent(err);

    if (err.code === AUTH_ERROR.FIRESTORE_PERMISSION) {
      // force version update after successful login to
      // fix random firestore 'missing permissions' bug
      const serviceWorker = useServiceWorkerStore();
      await serviceWorker.refreshApp();
      // todo: continue
    } else if (err.message === AUTH_ERROR.CLIENT_OFFLINE) {
      alert('Error connecting to the backend. Perhaps you are off-line? Click OK to refresh the app and retry.');
      window.location.reload();
    } else if (err.message === AUTH_ERROR.INVITATION_NOT_FOUND) {
      const correctRegionIsFound = await this.findAndSelectCorrectRegion({
        email: user.email,
        context,
      });

      if (!correctRegionIsFound && !(await findAndNotifyUserDeletion(user.email))) {
        notifyNegative(i18n.t('signup.signupComponent.notifyInvitationNotFound'));
      }
    } else if (err.message === AUTH_ERROR.INVITATION_WRONG_REGION) {
      this.changeLoaderState(false);
      const region = useRegionStore();
      const query = {
        region: region.region,
      };

      return payload.context.$router.push({
        name: 'signup',
        query,
      }).catch((redirectErr) => {
        logEvent(redirectErr);
        console.error(redirectErr);
      }).then(() => {
        notifyInfo(i18n.t('signup.signupComponent.notifyInvitationFoundInAnotherRegion'));
      });
    } else if (err.message === AUTH_ERROR.SIGN_UP_METHOD) {
      notifyNegative(i18n.t('signup.signupComponent.notifyWrongSignUpMethod'));
    }

    this.changeLoaderState(false);
  }, // handleAuthError()

  async changeCurrentClient(payload) {
    this.setCurrentClient(payload);

    const network = useNetworkStore();
    const sessions = useSessionsStore();
    const programs = useProgramsStore();
    const assessments = useAssessmentsStore();

    if (isEmpty(payload)) {
      sessionStorage.removeItem('selectedClient');

      return Promise.allSettled([
        programs.logout(),
        sessions.logout(),
        assessments.logout(),
        this.unbindClientAppointmentsInProgress(),
        this.unbindClientAppointmentsUpcoming(),
        this.unbindClientAppointmentsRecurring(),
      ]);
    }

    try {
      const bindingResult = await Promise.allSettled([
        programs.bindPrograms(),
        sessions.bindSessions(),
        sessions.bindSupervisingSessions(),
        assessments.logout(),
        ...((payload.type !== 'staff')
          ? [
            this.bindClientAppointmentsInProgress({
              clientId: this.currentClient.id,
              clinicId: this.currentClient.clinic,
            }),
            this.bindClientAppointmentsUpcoming({
              clientId: this.currentClient.id,
              clinicId: this.currentClient.clinic,
            }),
            this.bindClientAppointmentsRecurring({
              clientId: this.currentClient.id,
              clinicId: this.currentClient.clinic,
            }),
            ...(!network.offlineMode
              ? [await sessions.bindVideoCallSessions()]
              : []),
          ]
          : []
        ),
      ]);
      sessionStorage.setItem('selectedClient', payload.id);
      return bindingResult;
    } catch (e) {
      this.setCurrentClient({});
      sessionStorage.removeItem('selectedClient');
      notifyNegative(i18n.t('clients.errorSwitchingClient'));
      logEvent(e, {
        context: {
          context_data: { clientId: payload.id },
        },
      });
      return null;
    }
  }, // changeCurrentClient()

  async changeCurrentClientById(clientId) {
    const clientsDb = new ClientsDB();

    try {
      const client = await clientsDb.read(clientId);

      if (!isEmpty(client)) {
        return this.changeCurrentClient(client);
      }
    } catch (err) {
      console.error('Error loading a client', err);
      // Just ignore if client could not be loaded
    }
  }, // changeCurrentClientById()

  async selectDisabledClient({ clientId }) {
    this.setDeactivatedClientAllowedToView(clientId);
    sessionStorage.setItem('deactivatedClientAllowedToView', JSON.stringify(clientId));
    return this.changeCurrentClientById(clientId);
  }, // selectDisabledClient()

  changeDeactivatedClientAllowedToView(clientId) {
    this.setDeactivatedClientAllowedToView(clientId);
    sessionStorage.setItem('deactivatedClientAllowedToView', JSON.stringify(clientId));
  }, // changeDeactivatedClientAllowedToView()

  async findAndSelectCorrectRegion({ email, context }) {
    try {
      const { data } = await callFirebaseFunction('findUser', { email });

      // user found
      if (data) {
        const region = useRegionStore();
        await region.setRegion({ region: data.region, context });
        notifyWarning(i18n.t('login.userFoundInAnotherRegion'));
        return true;
      }

      return false;
    } catch (err) {
      logEvent(err);
      return false;
    }
  }, // findAndSelectCorrectRegion()

  async updateMessagingToken({ value, type } = {}) {
    const fcmTokens = cloneDeep(get(this.userDetails, 'fcmTokens', []));

    // @todo: why is state.userDetails.userId missing sometimes?
    if (!this.userDetails.userId || find(fcmTokens, { value })) {
      return;
    }

    fcmTokens.push({ value, type });

    const trimmedFcmTokens = fcmTokens.length > FCM_TOKEN_MAX_COUNT
      ? fcmTokens.slice(-FCM_TOKEN_MAX_COUNT)
      : fcmTokens;

    const usersDb = new UsersDB();

    await usersDb.set({ fcmTokens: trimmedFcmTokens }, this.userDetails.userId);
    this.setUserDetails({ ...this.userDetails, fcmTokens: trimmedFcmTokens });

    localStorage.setItem('currentMessagingToken', value);
  }, // updateMessagingToken()

  setPendingCredential(credential) {
    this.pendingCredential = credential;
  }, // setPendingCredential()

  updateCurrentClient(userData) {
    this.setCurrentClient(userData);
  }, // updateCurrentClient()

  async updateUserSettings(userSettings) {
    const userSettingsDb = new UserSettingsDB(this.userDetails.userId);
    await userSettingsDb.set(userSettings, 'settings');
    this.setUserSettings({ ...this.userSettings, ...userSettings });
  }, // updateUserSettings()

  async syncCurrentClient() {
    const clientsDB = new ClientsDB();
    const client = await clientsDB.read(this.currentClient.id);

    this.updateCurrentClient(client);
  }, // syncCurrentClient()

  async canUserCreateVideoSession() {
    if (this.currentClient.status !== 'active') {
      return false;
    }

    const { nextResourcesUpdateDate } = this.currentClient;

    if (nextResourcesUpdateDate && (new Date() > nextResourcesUpdateDate.toDate())) {
      await callFirebaseFunction('resetClientResources', {
        clinicId: this.userDetails.clinic,
      });

      await this.syncCurrentClient();

      const updatedUser = {
        ...this.currentClient,
        usedMinutes: 0,
      };
      this.updateCurrentClient(updatedUser);
    }

    // @todo At the moment we don't enforce TeleHealth limits
    return true;
  }, // canUserCreateVideoSession()

  changeLoaderState(payload) {
    this.showLoader = payload;
  }, // changeLoaderState()

  async updateClinicNotificationsSettings(payload) {
    try {
      this.setClinicNotificationSettings(payload);

      const clinicSettingsDB = new ClinicSettingsDB();
      await clinicSettingsDB.set({ notificationSettings: payload }, this.userDetails.clinic);
    } catch (err) {
      console.error('Update clinic notifications error:', err);
      logEvent(err);
    }
  }, // updateClinicNotificationsSettings()

  /**
   * Updates whether the treatment trajectory feature is enabled for the clinic
   * @param {boolean} isEnabled
   */
  async updateTrajectorySettingsEnabled(isEnabled) {
    const previousSettings = this.trajectorySettings;
    const updatedSettings = {
      ...(this.trajectorySettings || {}),
      isEnabled,
    };

    this.setTrajectorySettings(updatedSettings);

    try {
      const clinicSettingsDb = new ClinicSettingsDB();
      await clinicSettingsDb.update({
        id: this.userDetails.clinic,
        'trajectorySettings.isEnabled': isEnabled,
      });
    } catch (err) {
      this.setTrajectorySettings(previousSettings);
      throw err;
    }
  }, // updateTrajectorySettingsEnabled()

  /**
   * Updates the treatment trajectory status configurations for the clinic
   * @param {Object} statuses
   */
  async updateTrajectorySettingsStatuses(statuses) {
    const previousSettings = this.trajectorySettings;
    const updatedSettings = {
      ...(this.trajectorySettings || {}),
      statuses,
    };

    this.setTrajectorySettings(updatedSettings);

    try {
      const clinicSettingsDb = new ClinicSettingsDB();
      await clinicSettingsDb.update({
        id: this.userDetails.clinic,
        'trajectorySettings.statuses': statuses,
      });
    } catch (err) {
      this.setTrajectorySettings(previousSettings);
      throw err;
    }
  }, // updateTrajectorySettingsStatuses()
};
